import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// Common styled components for axis elements
const AxisLabel = styled.text`
  font-size: 12px;
  font-weight: 500;
  fill: var(--text-secondary);
`;

const XAxisLabel = styled(AxisLabel)`
  text-anchor: middle;
`;

// Modified YAxisLabel to handle text truncation
const YAxisLabel = styled(AxisLabel)`
  text-anchor: start;
  dominant-baseline: middle;
  cursor: default;
`;

const AxisTitle = styled.text`
  font-size: 14px;
  font-weight: 600;
  fill: var(--text-primary);
  text-anchor: middle;
`;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  padding-bottom: 20px;
  flex-direction: column;
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
`;

const FixedHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: var(--card-bg);
  z-index: 15;
  padding: 0.75rem 0;
  display: flex;
  justify-content: start;
  align-items: start;
  margin-top: 2rem;
  width: 100%;
`;

const ChartPlaceholder = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 400px;
  background: var(--card-highlight-bg);
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  box-sizing: border-box;
  position: relative;

  /* Enhanced WebKit scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--card-bg);
    border-radius: 6px;
    margin: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--chart-primary-translucent);
    border-radius: 6px;
    border: 2px solid var(--card-bg);
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--chart-primary);
    cursor: pointer;
  }

  &::-webkit-scrollbar-corner {
    background: var(--card-bg);
  }

  /* Firefox scrollbar styles */
  scrollbar-width: thin;
  scrollbar-color: var(--chart-primary-translucent) var(--card-bg);

  /* Add smooth scrolling for better UX */
  scroll-behavior: smooth;
`;

const ScrollableChartContent = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: start;
  padding: 0 10px 20px 0;
  position: relative;
`;

const TooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.1s ease;

  &.visible {
    opacity: 1;
  }

  .tooltip-date {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }

  .tooltip-count {
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
  }

  .tooltip-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 0.5rem;
    background-color: var(--chart-primary);
  }
`;

// New tooltip for displaying full date on hover
const DateTooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 101;
  opacity: 0;
  transition: opacity 0.1s ease;
  max-width: 300px;
  color: var(--text-primary);
  font-size: 0.875rem;

  &.visible {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--card-bg);
  opacity: 0.9;
  z-index: 25;
  border-radius: var(--border-radius);
`;

// Format date for better display in the chart
// The API sends plain EST days ("2026-07-14"). new Date() would read that as
// UTC midnight and show the previous day to anyone west of London, so build
// the date from its parts instead.
const parseDay = (dateString) => {
  const [y, m, d] = String(dateString).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (dateString) => {
  const date = parseDay(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Format full date for tooltip
const formatFullDate = (dateString) => {
  const date = parseDay(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

// Hook for responsive chart dimensions
const useResponsiveChart = (containerRef) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(300, width), // Minimum width of 300px
          height
        });
      }
    };

    // Initial calculation
    updateDimensions();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Also listen for window resize events
    window.addEventListener("resize", updateDimensions);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      window.removeEventListener("resize", updateDimensions);
    };
  }, [containerRef]);

  return dimensions;
};

// Function to truncate text with ellipsis - define only once and properly
const truncateText = (text, maxLength = 10) => {
  if (!text) return "";
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
};

const SurveyFrequencyChart = ({
  surveyData,
  isLoading = false,
  emptyStateMessage = "No survey data available"
}) => {
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    date: "",
    count: 0
  });

  const [dateTooltipState, setDateTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    fullDate: ""
  });

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const dateTooltipRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Use the responsive hook to get current dimensions
  const dimensions = useResponsiveChart(containerRef);

  if (!surveyData || surveyData.length === 0) {
    return (
      <ChartContainer ref={containerRef}>
        <ChartPlaceholder className="empty">
          <EmptyState>
            <div>📊</div>
            <div>{emptyStateMessage}</div>
          </EmptyState>
        </ChartPlaceholder>
      </ChartContainer>
    );
  }

  // Sort data by date for chronological display
  const sortedData = [...surveyData].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Fixed width for date labels
  const FIXED_LABEL_WIDTH = 90;
  const MAX_CHARS = 12; // Maximum characters before truncation

  const margin = {
    top: 20,
    right: 60, // Increased right margin to accommodate end labels
    bottom: 50,
    left: FIXED_LABEL_WIDTH + 20
  };
  const chartWidth = dimensions.width || 600; // Fallback width if dimensions not ready
  const width = chartWidth - margin.left - margin.right;

  const barHeight = 30;
  const barPadding = 10;
  const barSpacing = barHeight + barPadding;

  const contentHeight = Math.max(
    350,
    sortedData.length * barSpacing + margin.top + margin.bottom
  );

  // Calculate scale for bar widths
  const maxCount = Math.max(...sortedData.map((d) => d.count));
  const xScale = (value) => (value / maxCount) * width;
  const yScale = (index) => margin.top + index * barSpacing;

  // Generate evenly spaced tick values
  const tickCount = 5;
  const xTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((maxCount / tickCount) * i)
  );

  // Handle bar hover for tooltip with precise mouse position
  const handleBarHover = (event, d, i) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set tooltip state with exact mouse position
    setTooltipState({
      visible: true,
      x: mouseX + 10, // Small offset to not cover cursor
      y: mouseY + 10, // Small offset to not cover cursor
      date: d.date,
      count: d.count
    });
  };

  const handleBarLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // Handle date label hover for full date tooltip
  const handleDateHover = (event, fullDate) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set date tooltip state
    setDateTooltipState({
      visible: true,
      x: mouseX + 10,
      y: mouseY - 20,
      fullDate
    });
  };

  const handleDateLeave = () => {
    setDateTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // Generate different colors for each date to make them visually distinct
  // Similar to getPharmacyColor in PharmacyOrderChart
  const getDateColor = (index) => {
    return "#AEC7ED";
    const colors = [
      "#4338ca", // Deep indigo
      "#0284c7", // Deep blue
      "#ea580c", // Burnt orange
      "#65a30d", // Olive green
      "#0891b2", // Cyan
      "#9333ea", // Purple
      "#e11d48", // Rose
      "#0d9488", // Teal
      "#d97706", // Amber
      "#2563eb", // Blue
      "#7c3aed", // Violet
      "#db2777" // Pink
    ];

    return colors[index % colors.length];
  };

  return (
    <ChartContainer ref={containerRef}>
      <FixedHeader>
        <div style={{ width: "100%", position: "relative" }}>
          <svg width="100%" height="40" style={{ overflow: "visible" }}>
            <g transform={`translate(${margin.left},15)`}>
              {/* X-Axis title */}
              <AxisTitle x={width / 2} y="-30">
                Number of Surveys
              </AxisTitle>

              {/* X-Axis labels without tick lines */}
              {xTicks.map((tick, i) => (
                <g key={i}>
                  <XAxisLabel x={xScale(tick)} y="20">
                    {tick}
                  </XAxisLabel>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </FixedHeader>

      <ChartPlaceholder ref={scrollContainerRef}>
        {isLoading && (
          <LoadingOverlay>
            <div className="loading-spinner" />
          </LoadingOverlay>
        )}

        <ScrollableChartContent ref={chartRef}>
          <svg
            width="100%"
            height={contentHeight}
            viewBox={`0 0 ${chartWidth} ${contentHeight}`}
            preserveAspectRatio="xMinYMin meet"
          >
            {/* Create a fixed width background for the label area to visually separate labels */}
            <rect
              x="0"
              y="0"
              width={margin.left}
              height={contentHeight}
              fill="transparent"
            />

            {/* Separate group for date labels to ensure they don't go under bars */}
            <g>
              {sortedData.map((d, i) => {
                const formattedDate = formatDate(d.date);
                const fullDate = formatFullDate(d.date);

                return (
                  <g key={`label-${i}`}>
                    {/* Hover area for date labels */}
                    <rect
                      x={margin.left - FIXED_LABEL_WIDTH}
                      y={yScale(i)}
                      width={FIXED_LABEL_WIDTH - 10}
                      height={barHeight}
                      fill="transparent"
                      onMouseOver={(e) => handleDateHover(e, fullDate)}
                      onMouseLeave={handleDateLeave}
                    />

                    {/* Date label (y-axis) with fixed width and truncation - left aligned */}
                    <YAxisLabel
                      x={margin.left - FIXED_LABEL_WIDTH}
                      y={yScale(i) + barHeight / 2}
                      onMouseOver={(e) => handleDateHover(e, fullDate)}
                      onMouseLeave={handleDateLeave}
                    >
                      {truncateText(formattedDate, 10)}
                    </YAxisLabel>
                  </g>
                );
              })}
            </g>

            <g transform={`translate(${margin.left},0)`}>
              {/* Horizontal grid lines */}
              {sortedData.map((_, i) => (
                <line
                  key={`grid-${i}`}
                  x1="0"
                  y1={yScale(i) + barHeight / 2}
                  x2={width}
                  y2={yScale(i) + barHeight / 2}
                  stroke="var(--border-color)"
                  strokeOpacity="0.2"
                  strokeDasharray="4,4"
                />
              ))}

              {/* Vertical grid lines */}
              {xTicks.map((tick, i) => (
                <line
                  key={`vgrid-${i}`}
                  x1={xScale(tick)}
                  y1="0"
                  x2={xScale(tick)}
                  y2={contentHeight - margin.bottom}
                  stroke="var(--border-color)"
                  strokeOpacity="0.2"
                  strokeDasharray="4,4"
                />
              ))}

              {/* Bars with direct mouse event handlers */}
              {sortedData.map((d, i) => {
                const barColor = getDateColor(i);

                return (
                  <g key={i}>
                    {/* Bar with direct mouse event handlers */}
                    <rect
                      x="0"
                      y={yScale(i)}
                      width={xScale(d.count)}
                      height={barHeight}
                      fill={barColor}
                      rx="4"
                      opacity="0.85"
                      className="chart-bar"
                      onMouseMove={(e) => handleBarHover(e, d, i)}
                      onMouseLeave={handleBarLeave}
                    />

                    {/* Count label at the end of the bar */}
                    <text
                      x={xScale(d.count) + 10} // Position text after the end of bar
                      y={yScale(i) + barHeight / 2 + 5} // Center vertically with the bar
                      fill="var(--text-primary)" // Use primary text color for better visibility
                      fontSize="14"
                      fontWeight="600" // Make it bold for emphasis
                      dominantBaseline="middle"
                      pointerEvents="none" /* Prevent text from interfering with mouse events */
                    >
                      {d.count}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Screen reader description */}
            <desc>
              Horizontal bar chart showing survey frequency over time.
              {sortedData
                .map(
                  (d) =>
                    `On ${formatDate(d.date)}, there were ${d.count} surveys.`
                )
                .join(" ")}
            </desc>
          </svg>

          {/* Tooltip - positioned absolutely within ScrollableChartContent */}
          <TooltipContainer
            ref={tooltipRef}
            className={tooltipState.visible ? "visible" : ""}
            style={{
              position: "absolute",
              left: `${tooltipState.x}px`,
              top: `${tooltipState.y}px`
            }}
          >
            <div className="tooltip-date">{formatDate(tooltipState.date)}</div>
            <div className="tooltip-count">
              <div
                className="tooltip-color"
                style={{
                  backgroundColor: getDateColor(
                    sortedData.findIndex((d) => d.date === tooltipState.date)
                  )
                }}
              />
              <span>{tooltipState.count} Surveys</span>
            </div>
          </TooltipContainer>

          {/* Date tooltip for showing full date on hover */}
          <DateTooltipContainer
            ref={dateTooltipRef}
            className={dateTooltipState.visible ? "visible" : ""}
            style={{
              position: "absolute",
              left: `${dateTooltipState.x}px`,
              top: `${dateTooltipState.y}px`
            }}
          >
            {dateTooltipState.fullDate}
          </DateTooltipContainer>
        </ScrollableChartContent>
      </ChartPlaceholder>
    </ChartContainer>
  );
};

export default SurveyFrequencyChart;
